import { roundMoney } from '@tcr-erp/shared';
import {
  getPrintMethod,
  printAuto,
  type ReceiptData,
} from '@/utils/printSettings';
import {
  receiptTemplateService,
  type ReceiptTemplateKind,
} from '@/services/receiptTemplate.service';
import { orderService } from '@/services/order.service';
import { formatVND, formatDecimal, formatDateTime } from '@/utils/format';
import { ORDER_PAYMENT_LABEL } from '../parts/constants';
import type { TenantOrder } from '@/mocks/orders';

/** TCR-1332: đơn lấy tại chi nhánh mới có "Khách đưa"/"Tiền thừa" trên hoá
 *  đơn in — đơn giao hàng (kể cả tự ship) không thu tiền mặt tại quầy nên
 *  không có số này. Đọc `delivery_method` trước, `shipping.method` cho đơn
 *  cũ chưa có field top-level (TCR-1019 Task 20). */
function isPickupOrder(row: TenantOrder): boolean {
  return row.delivery_method === 'pickup' || row.shipping?.method === 'pickup';
}

/** `unit_price` là field legacy không có trong schema `OrderItem` hiện hành
 *  (xem `src/mocks/orders.ts`) — intersect thêm để `itemGrossPrice` fallback
 *  đúng cho đơn cũ mà KHÔNG cần `any`. */
type ReceiptLineItem = NonNullable<TenantOrder['items']>[number] & {
  unit_price?: number;
};

/** TCR-1332 (QA vòng 2): đơn giá in trên DÒNG HÀNG phải là giá GỐC TRƯỚC
 *  chiết khấu dòng, không phải `price` (giá đã trừ CK dòng) — vì "Tạm tính"
 *  của hoá đơn là `summary.original_price` (gộp giá gốc) còn "Giảm giá" đã
 *  bao gồm CẢ chiết khấu dòng. Lấy `price` làm dòng hàng thì tổng dòng
 *  (540.000) lệch hẳn với Tạm tính (600.000) dù TỔNG vẫn đúng — chính là lỗi
 *  QA bắt. `original_price` (TCR-1073) có thể thiếu trên đơn cũ ⇒ ngã về
 *  `price`; KHÔNG suy giá gốc từ chiết khấu tổng của đơn. */
function itemGrossPrice(it: ReceiptLineItem): number {
  const original = it.original_price;
  if (original !== undefined && original !== null && Number(original) > 0) {
    return Number(original);
  }
  return it.price ?? it.unit_price ?? 0;
}

/** TCR-1332 (receipt/POS): liệt kê từng khoản giảm giá theo tên chương
 *  trình cụ thể cho dòng "Giảm giá" GỘP của mẫu receipt — KHÔNG dùng cho
 *  mẫu invoice nữa (invoice tách riêng Tổng chiết khấu/Khuyến mãi tự động,
 *  xem `buildPromotionItems`/`buildVoucherItems` bên dưới). KHÔNG suy đoán
 *  1 dòng "Voucher" gộp từ `summary.voucher_discount` khi `applied_vouchers`
 *  rỗng — đã gặp dữ liệu demo nơi field này thực ra trùng với 1 khoản trong
 *  `applied_promotions` (BE gộp nhầm), suy đoán thêm dòng sẽ hiện trùng số. */
function buildDiscountItems(row: TenantOrder): Array<{ label: string; amount: number }> {
  const summary = row.summary;
  const result: Array<{ label: string; amount: number }> = [];

  if (summary?.direct_discount) {
    result.push({ label: 'Chiết khấu trực tiếp', amount: summary.direct_discount });
  }

  for (const v of summary?.applied_vouchers ?? []) {
    if (!v.discount_amount) continue;
    const code = v.voucher_code || v.code;
    result.push({ label: code ? `Voucher ${code}` : 'Voucher', amount: v.discount_amount });
  }

  for (const p of summary?.applied_promotions ?? []) {
    if (!p.discount_amount) continue;
    result.push({ label: p.name, amount: p.discount_amount });
  }

  return result;
}

/** TCR-1328 (invoice): "Khuyến mãi tự động" — cùng danh sách nguồn với card
 *  "Tổng hợp tài chính" ở trang chi tiết đơn (`summary.applied_promotions`). */
function buildPromotionItems(row: TenantOrder): Array<{ label: string; amount: number }> {
  return (row.summary?.applied_promotions ?? [])
    .filter((p) => p.discount_amount)
    .map((p) => ({ label: p.name, amount: p.discount_amount }));
}

/** TCR-1328 (invoice): voucher thật đã áp dụng, liệt kê theo mã — tách
 *  riêng khỏi "Khuyến mãi tự động" vì khác bản chất (khách nhập mã, không
 *  phải hệ thống tự áp). */
function buildVoucherItems(row: TenantOrder): Array<{ label: string; amount: number }> {
  return (row.summary?.applied_vouchers ?? [])
    .filter((v) => v.discount_amount)
    .map((v) => ({ label: v.voucher_code || v.code || 'Voucher', amount: v.discount_amount }));
}

/** TCR-1328 (invoice): "Thuế VAT: chi tiết" — chỉ liệt kê từng mức thuế
 *  suất khi đơn có NHIỀU HƠN 1 mức (đơn chỉ 1 mức thuế thì 1 dòng tổng
 *  "Thuế VAT" đã đủ rõ, tách riêng thêm 1 dòng con trùng số chỉ gây rối). */
function buildVatBreakdown(row: TenantOrder): Array<{ label: string; amount: number }> {
  const breakdowns = row.summary?.tax_breakdowns ?? [];
  if (breakdowns.length <= 1) return [];
  // `vat_rate`/`vat_amount` kiểu `unknown` trên TenantOrderTaxBreakdown — BE
  // có thể trả Decimal128 đã serialize thành chuỗi, phải ép Number() tường minh.
  return breakdowns.map((b) => ({
    label: `Thuế VAT ${Number(b.vat_rate)}%`,
    amount: Number(b.vat_amount),
  }));
}

/** Map TenantOrder → ReceiptData. Reused bởi list row action + detail page.
 *  Store info để rỗng — agent template hoặc browser fallback đều có default
 *  fallback "TCR-ERP". Nếu cần fill store name/address, caller truyền vào
 *  `storeOverride`. */
export function orderToReceiptData(
  row: TenantOrder,
  storeOverride?: { storeName?: string; storeAddress?: string; storePhone?: string },
): ReceiptData {
  const items =
    row.items?.map((it) => ({
      name: it.product_name || (it as any).name || '',
      sku: it.sku || '',
      qty: it.qty ?? (it as any).quantity ?? 1,
      price: itemGrossPrice(it),
    })) ?? [];

  const isPickup = isPickupOrder(row);
  const originalPrice = row.summary?.original_price ?? (row.subtotal ?? row.total_amount) + (row.discount ?? 0);

  return {
    orderCode: row.order_number,
    storeName: storeOverride?.storeName ?? '',
    storeAddress: storeOverride?.storeAddress ?? '',
    storePhone: storeOverride?.storePhone ?? '',
    items,
    subtotal: originalPrice,
    discount: row.discount ?? 0,
    total: row.total_amount,
    paymentMethod:
      (row.payment_method && (ORDER_PAYMENT_LABEL[row.payment_method] ?? row.payment_method)) ??
      '',
    amountPaid: isPickup && row.cash_received ? row.cash_received : undefined,
    change: row.change_amount,
    cashier: row.cashier?.name ?? '-',
    date: row.created_at ?? new Date().toISOString(),
  } as ReceiptData;
}

/** Map TenantOrder → render data shape mà BE preview API expect (giống
 *  RenderData ở app/modules/receipt-template/providers/receipt-render.service.ts). */
export function toRenderData(
  row: TenantOrder,
  storeOverride?: { storeName?: string; storeAddress?: string; storePhone?: string },
): Record<string, unknown> {
  const buyer = row.buyer_invoice_info;
  const customerFallback = row.customer;
  const isPickup = isPickupOrder(row);
  const originalPrice = row.summary?.original_price ?? (row.subtotal ?? row.total_amount) + (row.discount ?? 0);

  return {
    store: {
      name: storeOverride?.storeName,
      address: storeOverride?.storeAddress,
      phone: storeOverride?.storePhone,
    },
    customer: {
      name: buyer?.name || customerFallback?.name,
      phone: (buyer as any)?.phone || customerFallback?.phone,
      address: buyer?.address || (customerFallback as any)?.address,
      tax_code: buyer?.tax_id || (buyer as any)?.tax_code || (customerFallback as any)?.tax_code,
    },
    order: {
      code: row.order_number,
      date: row.created_at ? formatDateTime(row.created_at) : '',
      cashier: row.cashier?.name,
      items: (row.items ?? []).map((it, i) => {
        const qty = it.qty ?? (it as any).quantity ?? 1;
        const price = itemGrossPrice(it);
        // TCR-2224 — chuẩn MISA: thành tiền = làm tròn (đơn giá × SL), khử nhiễu float.
        const lineTotal = roundMoney(qty * price);
        return {
          index: i + 1,
          name: it.product_name || (it as any).name || '',
          unit: (it as any).unit || 'Cái',
          sku: it.sku || '',
          qty,
          price,
          // TCR-2224 — đơn giá có thể lẻ (tối đa 2 chữ số); `formatVND` làm tròn mất phần lẻ.
          // `\u00A0₫` khớp đúng ký hiệu `formatVND` (Intl vi-VN) in ra.
          price_formatted: `${formatDecimal(price)}\u00A0₫`,
          line_total: lineTotal,
          line_total_formatted: formatVND(lineTotal),
        };
      }),
      // Legacy — mẫu receipt/POS (TCR-1332) dùng dòng "Giảm giá" gộp.
      original_price: originalPrice,
      subtotal: row.subtotal ?? row.total_amount,
      discount: row.discount ?? 0,
      discount_items: buildDiscountItems(row),
      // TCR-1328 — mẫu invoice tách riêng từng nhóm giống card "Tổng hợp
      // tài chính" ở trang chi tiết đơn.
      gross_amount: originalPrice,
      direct_discount: row.summary?.direct_discount || undefined,
      promotion_discount: row.summary?.applied_promotions
        ?.reduce((sum, p) => sum + (p.discount_amount || 0), 0) || undefined,
      promotion_items: buildPromotionItems(row),
      voucher_discount: row.summary?.applied_vouchers
        ?.reduce((sum, v) => sum + (v.discount_amount || 0), 0) || undefined,
      voucher_items: buildVoucherItems(row),
      // TCR-1352 (vòng 2): "Điểm tích luỹ quy đổi" — cùng nguồn với
      // `loyaltyRedeemValue` của `OrderInfoPanel` (chi tiết/popup/sửa đơn),
      // trước đây thiếu ở mẫu invoice khiến hoá đơn in tự mâu thuẫn số.
      loyalty_discount: row.summary?.loyalty_discount || undefined,
      surcharge: row.surcharge_amount || undefined,
      vat: row.summary?.vat,
      vat_breakdown: buildVatBreakdown(row),
      total: row.total_amount,
      payment_method: row.payment_method
        ? ORDER_PAYMENT_LABEL[row.payment_method] ?? row.payment_method
        : '',
      is_pickup: isPickup,
      amount_paid: row.cash_received,
      change: row.change_amount,
    },
  };
}

/** In template với 1 đơn — fetch default theo `kind`, render qua BE preview
 *  API, in theo setting localStorage:
 *  - method='browser' (default cho invoice) → iframe +
 *    window.print(), user chọn máy in trong dialog hệ thống.
 *  - method='agent' (default cho receipt/label) → printAuto với
 *    ESC/POS template (chỉ kind=receipt+label support; invoice
 *    fallback browser kèm warn).
 *
 *  Default `kind='receipt'`. Caller pass 'invoice' / 'label'.
 */
export async function printOrderReceipt(
  row: TenantOrder,
  storeOverride?: { storeName?: string; storeAddress?: string; storePhone?: string },
  kind: ReceiptTemplateKind = 'receipt',
): Promise<{ ok: boolean; method: 'browser' | 'agent' }> {
  // TCR-1332: luôn fetch chi tiết đơn (không chỉ khi thiếu items) — `row` từ
  // bảng danh sách Orders không có `payment.cash_received`/breakdown khuyến
  // mãi đầy đủ để build đúng "Khách đưa"/"Giảm giá" trên hoá đơn in.
  let targetRow = row;
  if (targetRow._id) {
    try {
      const detail = await orderService.getDetail(targetRow._id);
      if (detail) {
        targetRow = {
          ...targetRow,
          ...detail,
          items: detail.items?.length ? detail.items : targetRow.items,
          buyer_invoice_info: detail.buyer_invoice_info || targetRow.buyer_invoice_info,
          customer: detail.customer || targetRow.customer,
        };
      }
    } catch (err) {
      console.warn('Lỗi fetch chi tiết đơn hàng cho in ấn:', err);
    }
  }

  // Packing box labels are rendered by the receipt-template preview flow;
  // there is no order-level agent print payload for this kind yet.
  const method = kind === 'packing_box_label' ? 'browser' : getPrintMethod(kind);
  const data = toRenderData(targetRow, storeOverride);

  // Path 1: agent (chỉ wire cho receipt + label qua printAuto). Invoice
  // chưa có agent path → fallback browser kèm console warn.
  if (method === 'agent' && (kind === 'receipt' || kind === 'label')) {
    if (kind === 'receipt') {
      try {
        const res = await printAuto({
          type: 'receipt',
          data: orderToReceiptData(targetRow, storeOverride),
          width: 80,
          openDrawerAfter: true,
        });
        return { ok: res.ok, method: 'agent' };
      } catch (err) {
        console.warn('Lỗi in qua hardware agent, fallback sang browser:', err);
        // Fallthrough sang Path 2 (browser print)
      }
    }
    // label-kind through agent — printAuto with type='label' yêu cầu
    // template + LabelData. Hiện không sẵn sàng ở Orders flow → fallback browser.
  }

  if (method === 'agent') {
    console.warn(
      `[print] kind=${kind} setting=agent chưa có agent path — fallback browser print dialog`,
    );
  }

  // Path 2: browser (default cho invoice + agent unsupported).
  try {
    const tpl = await receiptTemplateService.getDefault(kind);
    if (tpl) {
      const html = await receiptTemplateService.preview(tpl._id, data);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(iframe);
      iframe.srcdoc = html;
      await new Promise((r) => {
        iframe.onload = () => r(null);
      });
      iframe.contentWindow?.focus();
      if (typeof window !== 'undefined' && !(window as any).__MOCK_PRINT__) {
        iframe.contentWindow?.print();
      }
      setTimeout(() => iframe.remove(), 10000);
      return { ok: true, method: 'browser' };
    }
  } catch (e) {
    console.warn('Template print fail, fallback legacy:', e);
  }

  // Legacy fallback (template fetch fail) — chỉ áp dụng cho receipt.
  if (kind === 'receipt') {
    const res = await printAuto(
      {
        type: 'receipt',
        data: orderToReceiptData(row, storeOverride),
        width: 80,
        openDrawerAfter: true,
      },
      { forceMethod: 'browser' },
    );
    return { ok: res.ok, method: 'browser' };
  }
  throw new Error(`Không có template default cho kind=${kind}`);
}
